import { computed, type ComputedRef } from 'vue';

import { SpotStatus, type Nullable } from '@nova/core';
import { parseApiDateTime } from '@/helpers/checkSpotPanelDuration';
import type { SpotEventData } from '@/composables/useEventBasedDialog';
import type {
  YardSpotDetails,
  YardSpotAssignmentDetails,
  YardSpotReserveDetails
} from '@satellite/composables/useYardSpotPresenter';

export interface SpotStatusStrategyParams {
  spotDetail: ComputedRef<Nullable<YardSpotDetails>>;
  activeSpotAssignment: ComputedRef<Nullable<YardSpotAssignmentDetails>>;
  activeSpotReserve: ComputedRef<Nullable<YardSpotReserveDetails>>;
  latestCloseEvent: ComputedRef<Nullable<SpotEventData>>;
  latestOpenEvent: ComputedRef<Nullable<SpotEventData>>;
  spotStatus: ComputedRef<SpotStatus>;
}

export interface SpotStatusStrategy {
  color: ComputedRef<string>;
  icon: ComputedRef<string>;
  isTonal: ComputedRef<boolean>;
  durationCaptionKey: ComputedRef<Nullable<string>>;
  referenceDate: ComputedRef<Nullable<Date>>;
}

function useOpenSpotStatusStrategy(params: SpotStatusStrategyParams): SpotStatusStrategy {
  const referenceDate = computed(
    () =>
      parseApiDateTime(params.latestOpenEvent.value?.createDateTime) ||
      parseApiDateTime(params.spotDetail.value?.createDateTime) ||
      parseApiDateTime(params.spotDetail.value?.lastChangedDateTime) ||
      null
  );

  return {
    color: computed(() => 'success'),
    icon: computed(() => 'mdi-check'),
    isTonal: computed(() => true),
    durationCaptionKey: computed(() => 'checkSpotPanel.duration.caption.open'),
    referenceDate
  };
}

function useReservedSpotStatusStrategy(params: SpotStatusStrategyParams): SpotStatusStrategy {
  const referenceDate = computed(() => {
    const reserve = params.activeSpotReserve.value;
    return reserve
      ? parseApiDateTime(reserve.createDateTime) ||
          parseApiDateTime(reserve.lastChangedDateTime) ||
          null
      : null;
  });

  return {
    color: computed(() => 'neutral-90'),
    icon: computed(() => 'mdi-lock-clock'),
    isTonal: computed(() => true),
    durationCaptionKey: computed(() => 'checkSpotPanel.duration.caption.reserved'),
    referenceDate
  };
}

function useOccupiedSpotStatusStrategy(params: SpotStatusStrategyParams): SpotStatusStrategy {
  const referenceDate = computed(() => {
    const assignment = params.activeSpotAssignment.value;
    return assignment
      ? parseApiDateTime(assignment.createDateTime) ||
          parseApiDateTime(assignment.lastChangedDateTime) ||
          null
      : null;
  });

  return {
    color: computed(() => 'neutral-90'),
    icon: computed(() => 'mdi-view-column'),
    isTonal: computed(() => true),
    durationCaptionKey: computed(() => 'checkSpotPanel.duration.caption.assigned'),
    referenceDate
  };
}

function useClosedSpotStatusStrategy(params: SpotStatusStrategyParams): SpotStatusStrategy {
  const referenceDate = computed(
    () =>
      parseApiDateTime(params.latestCloseEvent.value?.createDateTime) ||
      parseApiDateTime(params.spotDetail.value?.lastChangedDateTime) ||
      parseApiDateTime(params.spotDetail.value?.createDateTime) ||
      null
  );

  return {
    color: computed(() => 'error'),
    icon: computed(() => 'mdi-cancel'),
    isTonal: computed(() => false),
    durationCaptionKey: computed(() => 'checkSpotPanel.duration.caption.closed'),
    referenceDate
  };
}

function useDefaultSpotStatusStrategy(): SpotStatusStrategy {
  return {
    color: computed(() => 'default'),
    icon: computed(() => 'mdi-help-circle-outline'),
    isTonal: computed(() => false),
    durationCaptionKey: computed(() => null),
    referenceDate: computed(() => null)
  };
}

export function useSpotStatusStrategy(
  params: SpotStatusStrategyParams
): ComputedRef<SpotStatusStrategy> {
  const strategies: Partial<Record<SpotStatus, SpotStatusStrategy>> = {
    [SpotStatus.OPEN]: useOpenSpotStatusStrategy(params),
    [SpotStatus.RESERVED_MANUAL]: useReservedSpotStatusStrategy(params),
    [SpotStatus.RESERVED_AUTOMATED]: useReservedSpotStatusStrategy(params),
    [SpotStatus.ASSIGNED]: useOccupiedSpotStatusStrategy(params),
    [SpotStatus.CLOSED]: useClosedSpotStatusStrategy(params),
    [SpotStatus.UNKNOWN]: useDefaultSpotStatusStrategy()
  };

  const defaultStrategy = useDefaultSpotStatusStrategy();

  return computed(() => strategies[params.spotStatus.value] ?? defaultStrategy);
}
